import React, { useEffect, useMemo, useState } from 'react';
import { StepProps } from '../MultiStepModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { clientsApi } from '../../../utils/api';
import { UserIcon, BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatPaymentTerms(value?: string) {
  if (!value) return 'Not set';
  switch (value) {
    case 'due-on-receipt':
      return 'Due on receipt';
    case 'net7':
      return 'Net 7 days';
    case 'net14':
      return 'Net 14 days';
    case 'net30':
      return 'Net 30 days';
    case 'net60':
      return 'Net 60 days';
    default:
      return value;
  }
}

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  address: string;
  postcode?: string;
}

interface LineItem {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

const InvoiceReviewStep: React.FC<StepProps> = ({ data, onDataChange }) => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [isNewCustomer, setIsNewCustomer] = useState(data.isNewCustomer || false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [customerSearch, setCustomerSearch] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoadingClients(true);
        const response = await clientsApi.getAll();
        setClients(response.data || []);
      } catch (error) {
        console.error('Error fetching clients for invoice:', error);
        showToast('Failed to load clients', 'error');
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Line items helpers
  const lineItemsRaw = (data as any)?.lineItems;
  const lineItemDescription = (data as any)?.description;
  const lineItemQuantity = (data as any)?.quantity;
  const lineItemSubtotal = (data as any)?.subtotal;

  const lineItems: LineItem[] = useMemo(() => {
    const existing = lineItemsRaw;
    if (Array.isArray(existing) && existing.length > 0) return existing as LineItem[];
    return [
      {
        id: 'item-1',
        description: lineItemDescription || '',
        quantity: lineItemQuantity || '1',
        unitPrice: lineItemSubtotal || '',
      },
    ] as LineItem[];
  }, [lineItemsRaw, lineItemDescription, lineItemQuantity, lineItemSubtotal]);

  const handleLineItemChange = (id: string, field: keyof LineItem, value: string) => {
    const updated = lineItems.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    onDataChange({
      lineItems: updated,
    });
  };

  const handleAddLineItem = () => {
    const newItem: LineItem = {
      id: `item-${Date.now()}`,
      description: '',
      quantity: '1',
      unitPrice: '',
    };
    onDataChange({
      lineItems: [...lineItems, newItem],
    });
  };

  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length === 1) return;
    onDataChange({
      lineItems: lineItems.filter((item) => item.id !== id),
    });
  };

  const handleChange = (field: string, value: string) => {
    onDataChange({ [field]: value });
  };

  const handleCustomerInputChange = (field: string, value: string) => {
    onDataChange({ [field]: value });
  };

  const handleCustomerSelect = (customerId: string) => {
    const customer = clients.find((c) => c.id === customerId);
    if (!customer) return;
    onDataChange({
      customerId,
      customerName: customer.name,
      customerEmail: customer.email || '',
      customerPhone: customer.phone,
      customerAddress: customer.address,
      postcode: customer.postcode || '',
      isNewCustomer: false,
    });
    setIsNewCustomer(false);
  };

  const handleNewCustomerToggle = () => {
    const next = !isNewCustomer;
    setIsNewCustomer(next);
    onDataChange({
      isNewCustomer: next,
      customerId: '',
      customerName: '',
      customerCompany: '',
      customerAddress: '',
      customerPhone: '',
      customerEmail: '',
    });
  };

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity || '0') || 0;
      const price = parseFloat(item.unitPrice || '0') || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);
  const vatRate = parseFloat(data.vatRate || '0') || 0;
  const vatAmount = subtotal * (vatRate / 100);
  const total = subtotal + vatAmount;

  const invoiceNumber = data.invoiceNumber || 'INV-0000';
  const invoiceDate = data.invoiceDate || '';
  const dueDate = data.dueDate || '';
  const paymentTermsLabel = formatPaymentTerms(data.paymentTerms);

  const customerName = data.customerName || data.customerCompany || 'Customer name';
  const customerAddress = data.customerAddress || 'Customer address';
  const customerEmail = data.customerEmail || '';
  const customerPhone = data.customerPhone || '';

  const companyName = user?.companyName || 'Certs';
  const companyLogoUrl = user?.companyLogoUrl;

  const filteredClients = useMemo(() => {
    if (!customerSearch.trim()) return clients;
    const q = customerSearch.toLowerCase();
    return clients.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.email && c.email.toLowerCase().includes(q)) ||
      c.phone.toLowerCase().includes(q)
    );
  }, [clients, customerSearch]);

  return (
    <div className="step-form">
      <div className="step-form-content">
        <div className="invoice-step-layout">
          {/* Left: Invoice + customer inputs */}
          <form className="space-y-6 invoice-step-form-column">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Invoice Number *</label>
                <input
                  type="text"
                  className="form-input"
                  value={data.invoiceNumber || ''}
                  onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                  placeholder="e.g. INV-2024-001"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Terms</label>
                <select
                  className="form-select"
                  value={data.paymentTerms || 'net30'}
                  onChange={(e) => handleChange('paymentTerms', e.target.value)}
                >
                  <option value="due-on-receipt">Due on Receipt</option>
                  <option value="net7">Net 7 Days</option>
                  <option value="net14">Net 14 Days</option>
                  <option value="net30">Net 30 Days</option>
                  <option value="net60">Net 60 Days</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Invoice Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={invoiceDate}
                  onChange={(e) => handleChange('invoiceDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  min={invoiceDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="Describe the work completed..."
                value={data.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">VAT Rate (%)</label>
                <select
                  className="form-select"
                  value={data.vatRate || '20'}
                  onChange={(e) => handleChange('vatRate', e.target.value)}
                >
                  <option value="0">0% (Exempt)</option>
                  <option value="5">5% (Reduced)</option>
                  <option value="20">20% (Standard)</option>
                </select>
              </div>
            </div>

            {/* Line items */}
            <div className="form-group">
              <label className="form-label">Line Items</label>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-[2fr_0.8fr_0.9fr_0.9fr] bg-gray-50 px-3 py-2 text-[11px] font-medium text-gray-500 uppercase tracking-[0.14em]">
                  <span>Description</span>
                  <span className="text-right">Qty</span>
                  <span className="text-right">Unit Price</span>
                  <span className="text-right">Line Total</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {lineItems.map((item) => {
                    const qty = parseFloat(item.quantity || '0') || 0;
                    const price = parseFloat(item.unitPrice || '0') || 0;
                    const lineTotal = qty * price;
                    return (
                      <div
                        key={item.id}
                        className="grid grid-cols-[2fr_0.8fr_0.9fr_0.9fr] items-center gap-2 px-3 py-2 text-xs text-gray-700"
                      >
                        <div className="pr-2">
                          <input
                            type="text"
                            className="form-input text-xs"
                            placeholder="e.g. Boiler service"
                            value={item.description}
                            onChange={(e) =>
                              handleLineItemChange(item.id, 'description', e.target.value)
                            }
                          />
                        </div>
                        <div className="text-right">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="form-input text-xs text-right"
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(item.id, 'quantity', e.target.value)
                            }
                          />
                        </div>
                        <div className="text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-input text-xs text-right"
                            value={item.unitPrice}
                            onChange={(e) =>
                              handleLineItemChange(item.id, 'unitPrice', e.target.value)
                            }
                          />
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <span className="whitespace-nowrap text-gray-900">
                            {lineTotal ? formatCurrency(lineTotal) : '—'}
                          </span>
                          {lineItems.length > 1 && (
                            <button
                              type="button"
                              className="text-xs text-gray-400 hover:text-red-500"
                              onClick={() => handleRemoveLineItem(item.id)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-secondary mt-2 text-xs"
                onClick={handleAddLineItem}
              >
                Add line item
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                rows={2}
                placeholder="Payment instructions, bank details, or terms..."
                value={data.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </div>

            {/* Customer information section */}
            <div className="pt-2 border-t border-gray-200 space-y-4">
              <div className="form-group">
                <div className="customer-type-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${!isNewCustomer ? 'active' : ''}`}
                    onClick={() => !isNewCustomer || handleNewCustomerToggle()}
                  >
                    <UserIcon className="w-4 h-4 mr-2" />
                    Existing Customer
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${isNewCustomer ? 'active' : ''}`}
                    onClick={() => isNewCustomer || handleNewCustomerToggle()}
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    New Customer
                  </button>
                </div>
              </div>

              {!isNewCustomer ? (
                <div className="form-group">
                  <label className="form-label">Select Customer *</label>
                  {loadingClients ? (
                    <div className="loading-state">
                      <p>Loading customers...</p>
                    </div>
                  ) : clients.length > 0 ? (
                    <>
                      <input
                        type="text"
                        className="form-input mb-2"
                        placeholder="Search by name, email, or phone"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                      />
                      <div className="customer-list-scroll">
                        <div className="customer-grid">
                          {filteredClients.map((customer) => (
                            <div
                              key={customer.id}
                              className={`customer-card ${
                                data.customerId === customer.id ? 'selected' : ''
                              }`}
                              onClick={() => handleCustomerSelect(customer.id)}
                            >
                              <div className="customer-avatar">
                                <UserIcon className="w-5 h-5" />
                              </div>
                              <div className="customer-info">
                                <h4 className="customer-name">{customer.name}</h4>
                                <p className="customer-phone">{customer.phone}</p>
                                <p className="customer-address">{customer.address}</p>
                              </div>
                            </div>
                          ))}
                          {filteredClients.length === 0 && (
                            <p className="text-xs text-gray-500 px-1 py-2">
                              No customers match that search.
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">
                      <p>No customers found. Create your first customer below.</p>
                      <button
                        type="button"
                        className="btn-primary mt-2"
                        onClick={handleNewCustomerToggle}
                      >
                        Add New Customer
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Customer Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="John Smith"
                        value={data.customerName || ''}
                        onChange={(e) => handleCustomerInputChange('customerName', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Company</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Smith Properties Ltd"
                        value={data.customerCompany || ''}
                        onChange={(e) => handleCustomerInputChange('customerCompany', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="+44 20 7123 4567"
                        value={data.customerPhone || ''}
                        onChange={(e) => handleCustomerInputChange('customerPhone', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="john@example.com"
                        value={data.customerEmail || ''}
                        onChange={(e) => handleCustomerInputChange('customerEmail', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <BuildingOfficeIcon className="w-4 h-4 inline mr-2" />
                      Property Address *
                    </label>
                    <textarea
                      className="form-textarea"
                      placeholder="123 Oak Street, London, SW1A 1AA"
                      value={data.customerAddress || ''}
                      onChange={(e) => handleCustomerInputChange('customerAddress', e.target.value)}
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          </form>

          {/* Right: Invoice preview */}
          <div className="invoice-step-preview-column bg-gray-50 rounded-xl border border-gray-200 p-4 lg:p-5">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 tracking-[0.18em] uppercase">
                    Invoice
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{invoiceNumber}</p>
                </div>
                <div className="flex items-start gap-3 text-right">
                  {companyLogoUrl && (
                    <div className="h-10 w-10 rounded-md border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                      <img
                        src={companyLogoUrl}
                        alt={`${companyName} logo`}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{companyName}</p>
                    {user?.email && (
                      <p className="text-xs text-gray-500">{user.email}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                <p className="font-medium text-gray-500">Invoice Date</p>
                <p className="font-medium text-gray-500">Due Date</p>
                <p className="text-gray-900">{invoiceDate || '—'}</p>
                <p className="text-gray-900">{dueDate || '—'}</p>
                <p className="font-medium text-gray-500 mt-2">Payment Terms</p>
                <p className="font-medium text-gray-500 mt-2">VAT Rate</p>
                <p className="text-gray-900">{paymentTermsLabel}</p>
                <p className="text-gray-900">{vatRate}%</p>
              </div>

              <div className="mt-2 rounded-lg bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Bill to</p>
                <p className="text-sm font-semibold text-gray-900">{customerName}</p>
                <p className="mt-1 text-xs text-gray-600 whitespace-pre-line">
                  {customerAddress}
                </p>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                  {customerEmail && <span>{customerEmail}</span>}
                  {customerPhone && <span>{customerPhone}</span>}
                </div>
              </div>

              <div className="mt-1 rounded-lg border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-[2fr_1fr_1fr] bg-gray-50 px-3 py-2 text-[11px] font-medium text-gray-500 uppercase tracking-[0.16em]">
                  <span>Description</span>
                  <span className="text-right">Qty</span>
                  <span className="text-right">Amount</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {lineItems.map((item) => {
                    const qty = parseFloat(item.quantity || '0') || 0;
                    const price = parseFloat(item.unitPrice || '0') || 0;
                    const lineTotal = qty * price;
                    return (
                      <div
                        key={item.id}
                        className="grid grid-cols-[2fr_1fr_1fr] px-3 py-2 text-xs text-gray-700"
                      >
                        <span>{item.description || 'Service description'}</span>
                        <span className="text-right">{qty || ''}</span>
                        <span className="text-right">
                          {lineTotal ? formatCurrency(lineTotal) : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1 text-xs text-gray-700">
                <div className="flex items-center justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>VAT ({vatRate}%)</span>
                  <span>{formatCurrency(vatAmount)}</span>
                </div>
                <div className="pt-1 mt-1 border-t border-gray-200 flex items-center justify-between text-sm font-semibold text-gray-900">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              {data.notes && (
                <div className="mt-2 border-t border-dashed border-gray-200 pt-2 text-[11px] text-gray-500">
                  {data.notes}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceReviewStep;
